import _ from 'lodash'

import type { CoreExportFormat, ReferencesMap } from '@wix/document-services-types'
import type { CssFile, ParanativeExportFormat } from './types'
import { getPluginsRunner } from './pluginsRunner'
import { generateGlobalStyles, generateStyle } from './formats/paranative/style/styleGenerator'
import { generateLayout } from './formats/paranative/layout/layoutGenerator'
import { generateElements } from './formats/paranative/elements/elementsGenerator'
import { generateStructure } from './formats/paranative/structure/structureGenerator'
import { createRegistryWithTypes } from './formats/paranative/createRegistry'
import { importStructure } from './formats/paranative/structure/structureImporter'
import { parseElementDefinitions } from './elementDefinition'
import { TagRegistry } from './tagRegistry'

export type { CssFile, ParanativeExportFormat, JsFile } from './types'
export * from './traversal'
export { ElementDefinitionBuilder, parseElementDefinitions } from './elementDefinition'

export interface ParanativeConverterAPI {
    converter: {
        pack(coreComponentExport: CoreExportFormat): ParanativeExportFormat
        unpack(paranativeExport: ParanativeExportFormat): CoreExportFormat
        generateGlobalStyles(componentExport: CoreExportFormat, references?: ReferencesMap): CssFile
    }
}

const packComponentDecorated = (componentExport: CoreExportFormat): CoreExportFormat => {
    getPluginsRunner(componentExport).pack()
    return componentExport
}

const unpackComponentDecorated = (componentCoreExport: CoreExportFormat): CoreExportFormat => {
    getPluginsRunner(componentCoreExport).unpack()
    return componentCoreExport
}

const exportParanative = (componentExport: CoreExportFormat): ParanativeExportFormat => {
    const registry = createRegistryWithTypes(componentExport)
    return {
        structure: generateStructure(componentExport, registry),
        style: generateStyle(componentExport),
        globalStyles: generateGlobalStyles(componentExport),
        layout: generateLayout(componentExport),
        elements: generateElements(registry),
    }
}

const createRegistryFromElements = (paranativeExport: ParanativeExportFormat): TagRegistry => {
    const { elements } = parseElementDefinitions(paranativeExport.elements.content)
    const registry = new TagRegistry()
    _.forEach(elements, ({ tag, definition }) => {
        registry.registerWithTag(definition.componentType, tag)
    })
    return registry
}

const importParanative = (paranativeExport: ParanativeExportFormat): CoreExportFormat => {
    const registry = createRegistryFromElements(paranativeExport)
    const { rootComponent, components } = importStructure(paranativeExport.structure, registry)
    // const style = parseStyle(paranativeExport.style, registry)

    return {
        version: '',
        rootComponent,
        components,
    }
}

export const converter: ParanativeConverterAPI['converter'] = {
    pack: (coreComponentExport: CoreExportFormat): ParanativeExportFormat => {
        const decoratedExport: CoreExportFormat = packComponentDecorated(coreComponentExport)
        return exportParanative(decoratedExport)
    },
    unpack: (paranativeExport: ParanativeExportFormat): CoreExportFormat => {
        const decoratedExport: CoreExportFormat = importParanative(paranativeExport) as CoreExportFormat
        return unpackComponentDecorated(decoratedExport)
    },
    generateGlobalStyles,
}
